import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import Select from 'react-select';
import {
	Button,
	Col,
	FormGroup,
	Label,
	Row,
} from 'reactstrap';

import { CheckboxInput, Input } from '@techchange/components';
import { LRS_ACTIONS, LRS_OBJECT_TYPES } from '@techchange/components';
import { messages } from '../../i18n';

import { RESOURCE_TYPES } from '../../constants';

import getActionMap from './utils/getActionMap';
import getObjectTypeMap from './utils/getObjectTypeMap';

const propTypes = {

	/**
	 * The default list of possible completion requirements,
	 * i.e. subconditions, for the completion rule.
	 */
	initialValues: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * True, if the subcondition is active.
		 */
		active: PropTypes.bool.isRequired,

		/**
		 * The subcondition action.
		 */
		action: PropTypes.string.isRequired,

		/**
		 * The type of the object for this subcondition.
		 */
		object_type: PropTypes.string.isRequired,

		/**
		 * The unique ID of the object for this subcondition (if any).
		 */
		object_id: PropTypes.string,

		/**
		 * The title of the object for this subcondition (if any).
		 */
		object_title: PropTypes.string,

		/**
		 * The type of the parent object for this subcondition (if any).
		 */
		parent_object_type: PropTypes.string,

		/**
		 * The unique ID of the parent object for this subcondition (if any).
		 */
		parent_object_id: PropTypes.string,

		/**
		 * The title of the parent object for this subcondition (if any).
		 */
		parent_object_title: PropTypes.string,

	})),

	/**
	 * The list of existing custom (non-default) completion requirements,
	 * i.e. subconditions, for the completion rule.
	 */
	customValues: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The subcondition ID.
		 */
		id: PropTypes.string.isRequired,

	})),

	/**
	 * The resource.
	 */
	resource: PropTypes.shape({

		/**
		 * The unique ID of the resource.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The resource type.
		 */
		type: PropTypes.oneOf([
			LRS_OBJECT_TYPES.CERTIFICATE,
			...Object.values(RESOURCE_TYPES),
		]),

		/**
		 * The resource title.
		 */
		title: PropTypes.string,

		/**
		 * The user UUID of the resource creator.
		 */
		creator_id: PropTypes.string,

		/**
		 * The resource scope.
		 */
		scope: PropTypes.oneOf(['session']).isRequired,

		/**
		 * The resource scope id.
		 */
		scope_id: PropTypes.string.isRequired,

		/**
		 * The resource organization id.
		 */
		organization_id: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * The rule for the resource.
	 */
	rule: PropTypes.shape({

		/**
		 * The unique ID of the rule.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The condition for the rule.
		 */
		condition: PropTypes.shape({

			/**
			 * The list of subconditions for the condition.
			 */
			subconditions: PropTypes.arrayOf(PropTypes.shape({

				/**
				 * The subcondition ID.
				 */
				id: PropTypes.string,

				/**
				 * Flag, true if subcondition is active.
				 */
				active: PropTypes.bool,

				/**
				 * The action.
				 */
				action: PropTypes.string,

				/**
				 * The object type.
				 */
				object_type: PropTypes.string,

				/**
				 * The object title.
				 */
				object_title: PropTypes.string,

				/**
				 * The object ID.
				 */
				object_id: PropTypes.string,

				/**
				 * The parent object type.
				 */
				parent_object_type: PropTypes.string,

				/**
				 * The parent object title.
				 */
				parent_object_title: PropTypes.string,

				/**
				 * The parent object ID.
				 */
				parent_object_id: PropTypes.string,

			})),

		}),

	}),

	/**
	 * The parent resource.
	 */
	parent: PropTypes.shape({

		/**
		 * The unique ID of the resource.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The resource type.
		 */
		type: PropTypes.string,

		/**
		 * The resource title.
		 */
		title: PropTypes.string,

		/**
		 * The user UUID of the resource creator.
		 */
		creator_id: PropTypes.string,

	}).isRequired,

	/**
	 * The current user's ID.
	 */
	userId: PropTypes.string.isRequired,

	/**
	 * Mapping of object type to a select option object. Used
	 *  to specify available object type options for custom subconditions.
	 */
	objectTypeOptionsMap: PropTypes.objectOf(PropTypes.shape({

		/**
		 * The option label.
		 */
		label: PropTypes.string.isRequired,

		/**
		 * The option value, i.e. the object type.
		 */
		value: PropTypes.string.isRequired,

	})),

	/**
	 * Mapping of object type to list of object options. Used to specify
	 *  available object options for custom subconditions.
	 */
	objectOptionsMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The option label.
		 */
		label: PropTypes.string.isRequired,

		/**
		 * The option value, i.e. the object id.
		 */
		value: PropTypes.string.isRequired,

		/**
		 * The id of the associated resource for the object, if any.
		 */
		resource_id: PropTypes.string,

		/**
		 * The object type.
		 */
		object_type: PropTypes.string.isRequired,

		/**
		 * The object ID.
		 */
		object_id: PropTypes.string,

		/**
		 * The object title.
		 */
		object_title: PropTypes.string,

		/**
		 * The parent object type.
		 */
		parent_object_type: PropTypes.string,

		/**
		 * The parent object ID.
		 */
		parent_object_id: PropTypes.string,

		/**
		 * The parent object title.
		 */
		parent_object_title: PropTypes.string,

	}))),

	/**
	 * Optional action override for the rule definition.
	 */
	ruleAction: PropTypes.string,

	/**
	 * Flag indicating if non-default subconditions can be created for this rule.
	 */
	allowCustomRequirements: PropTypes.bool,

	/**
	 * Flag to ignore default action options list for a given object type.
	 */
	overrideActionOptions: PropTypes.bool,

	/**
	 * Callback function to handle cancelling the editor for the completion rule.
	 */
	onCancel: PropTypes.func,

	/**
	 * Callback function to handle saving the completion rule.
	 */
	onSave: PropTypes.func.isRequired,

};

const RuleEditorForm = (props) => {

	const {
		initialValues,
		customValues,
		resource,
		parent,
		rule,
		userId,
		objectTypeOptionsMap,
		objectOptionsMap,
		ruleAction,
		allowCustomRequirements,
		overrideActionOptions,
		onCancel,
		onSave,
	} = props;

	const intl = useIntl();

	const [subconditions, setSubconditions] =
		useState(getInitialSubconditions(initialValues, customValues, rule));

	const [formValidation, setFormValidation] = useState({
		thresholdNaN: false,
	});

	// Get object_type -> action list mapping
	const actionMap = getActionMap(intl);

	// Get object_type -> object_type metadata mapping
	const objectTypeMap = getObjectTypeMap(intl);

	// Get list of allowed object_type options for the completion requirements
	const objectTypeOptions = objectTypeOptionsMap && Object.values(objectTypeOptionsMap);

	/**
	 * Handles changes to the list of possible completion requirements, i.e. subconditions.
	 *
	 * @param {number} index    The index of the subcondition to change.
	 * @param {object} updates  The data to update.
	 */
	const handleChangeSubcondition = (index, updates) => {

		// Create a copy of the subcondition data
		const updatedSubconditions = [...subconditions];

		// Update the subcondition data
		updatedSubconditions[index] =
			Object.assign({}, updatedSubconditions[index], updates);

		setSubconditions(updatedSubconditions);

	};

	const handleSubmit = (event) => {

		if (event) {
			event.preventDefault();
		}

		const containsErrors = Object.values(formValidation).reduce((acc, val) => acc || val);

		if (containsErrors) {
			return;
		}

		// Build the completion rule for the resource given the list of possible
		// completion requirements, i.e. subconditions.

		const ruleData = {
			active: false,
			creator_id: userId,
			scope: resource.scope,
			scope_id: resource.scope_id,
			organization_id: resource.organization_id,
			action: ruleAction || LRS_ACTIONS.COMPLETED,
			// Resource data
			object_type: resource.type,
			object: resource,
			object_id: resource.id,
			object_title: resource.title,
			object_user_id: resource.creator_id,
			// Parent resource data
			parent_object_id: parent.id,
			parent_object: parent,
			parent_object_type: parent.type,
			parent_object_title: parent.title,
			parent_object_user_id: parent.creator_id,
		};

		const conditionData = {
			active: false,
			operator: 'all',
			count: null,
			scope: resource.scope,
			scope_id: resource.scope_id,
			organization_id: resource.organization_id,
			subconditions: [],
		};

		// Check if we are updating an existing completion rule for the resource
		if (rule && rule.condition && rule.condition.subconditions) {

			ruleData.id = rule.id;

			conditionData.id = conditionData.id;

			// If so, start with all the existing subconditions initialized as inactive
			conditionData.subconditions = rule.condition.subconditions.map((subcondition) => {
				return Object.assign({}, subcondition, {
					active: false,
				});
			});

		}

		let isActive = false;

		// Loop through all of the up-to-date completion requirements, i.e. subconditions
		for (let i = 0, len = subconditions.length; i < len; i++) {

			const data = subconditions[i];

			// Keep track if we've seen at least one active requirement, i.e. rule should be active;
			isActive = isActive || data.active;

			// Check if there is an existing subcondition already selected
			const index = conditionData.subconditions.findIndex((subcondition) => {

				// Make sure the action matches
				if (subcondition.action !== data.action) {
					return false;
				}

				// Make sure the object type matches
				if (subcondition.object_type !== data.object_type) {
					return false;
				}

				// Make sure the object id matches
				if ((!subcondition.object_id && data.object_id) ||
					(subcondition.object_id && !data.object_id) ||
					(subcondition.object_id !== data.object_id)) {
					return false;
				}

				// Make sure the parent object type matches
				if ((!subcondition.parent_object_type && data.parent_object_type) ||
					(subcondition.parent_object_type && !data.parent_object_type) ||
					(subcondition.parent_object_type !== data.parent_object_type)) {
					return false;
				}

				// Make sure the parent object id matches
				if ((!subcondition.parent_object_id && data.parent_object_id) ||
					(subcondition.parent_object_id && !data.parent_object_id) ||
					(subcondition.parent_object_id !== data.parent_object_id)) {
					return false;
				}

				return true;

			});

			// Check if we need to update a subcondition
			if (index >= 0) {

				conditionData.subconditions[index] = Object.assign({},
					conditionData.subconditions[index], data);

			// Otherwise, create a new subcondition
			} else {

				conditionData.subconditions.push({
					active: data.active,
					action: data.action,
					object_title: data.object_title,
					object_type: data.object_type,
					object_id: data.object_id,
					parent_object_title: data.parent_object_title,
					parent_object_type: data.parent_object_type,
					parent_object_id: data.parent_object_id,
					meta: data.meta,
					scope: resource.scope,
					scope_id: resource.scope_id,
					organization_id: resource.organization_id,
				});

			}

		}

		// Check if the completion rule for the resource is active
		if (isActive) {
			ruleData.active = true;
			conditionData.active = true;
		}

		ruleData.condition = conditionData;

		onSave(ruleData);

	};

	const addRequirement = () => {

		// Choose default requirement
		for (const objectTypeOption of objectTypeOptions) {

			const objectType = objectTypeOption.value;
			const objectOptions = objectOptionsMap[objectType];

			if (objectOptions && objectOptions.length > 0) {

				const defaultAction = actionMap[objectType][0].value;
				const defaultObject = objectOptions[0];
				const defaultObjectId =
					defaultAction === LRS_ACTIONS.MARKED_COMPLETE
						? defaultObject.resource_id
						: defaultObject.object_id;

				// Create a new default subcondition
				const newSubcondition = {
					active: false,
					action: defaultAction,
					object_type: defaultObject.object_type,
					object_id: defaultObjectId,
					object_title: defaultObject.object_title,
					parent_object_type: defaultObject.parent_object_type,
					parent_object_id: defaultObject.parent_object_id,
					parent_object_title: defaultObject.parent_object_title,
				};

				setSubconditions((prev) => [...prev, newSubcondition]);

				break;

			}

		}

	};

	const onSelectAction = (index, fieldName, fieldValue) => {

		const updatedValues = {
			[fieldName]: fieldValue,
		};

		// Get object options for currently selected object type
		const currentSubcondition = subconditions[index];
		const objectOptions = objectOptionsMap && objectOptionsMap[currentSubcondition.object_type];

		// Check if we need to update the object_id given the newly selected action
		if (objectOptions && objectOptions.length > 0) {

			const currentObject = objectOptions.find((option) =>
				(option.object_id === currentSubcondition.object_id) ||
				(option.resource_id === currentSubcondition.object_id));

			const updatedObjectId =
				fieldValue === LRS_ACTIONS.MARKED_COMPLETE
					? currentObject.resource_id
					: currentObject.object_id;

			updatedValues.object_id = updatedObjectId;

		}

		handleChangeSubcondition(index, updatedValues);

	};

	const onSelectObjectType = (index, fieldName, fieldValue) => {

		const actions = actionMap[fieldValue];

		// Try to choose a default action
		let defaultAction = null;

		if (actions && actions.length > 0) {
			defaultAction = actions[0].value;
		}

		const objectOptions = objectOptionsMap[fieldValue];

		// Try to choose a default object (or parent object)
		if (objectOptions && objectOptions.length > 0) {

			const defaultObject = objectOptions[0];
			const defaultObjectId =
				defaultAction === LRS_ACTIONS.MARKED_COMPLETE
					? defaultObject.resource_id
					: defaultObject.object_id;

			handleChangeSubcondition(index, {
				action: defaultAction,
				object_type: defaultObject.object_type,
				object_id: defaultObjectId,
				object_title: defaultObject.object_title,
				parent_object_type: defaultObject.parent_object_type,
				parent_object_id: defaultObject.parent_object_id,
				parent_object_title: defaultObject.parent_object_title,
			});

		}

	};

	const onSelectObject = (index, fieldName, fieldValue, objectOptions, isParent) => {

		// Find the selected (parent) object id
		const selectedObject = objectOptions.find(
			(option) => option.value === fieldValue);

		const title = isParent ? 'parent_object_title' : 'object_title';
		const type = isParent ? 'parent_object_type' : 'object_type';

		// Update the form values to include the (parent) object title
		handleChangeSubcondition(index, {
			[title]: selectedObject[title],
			[type]: selectedObject[type],
			[fieldName]: fieldValue,
		});

	};

	const handleChangeThreshold = (index, fieldName, fieldValue) => {

		const parsedThreshold = parseInt(fieldValue, 10);

		setFormValidation((prev) => Object.assign({}, prev, {
			thresholdNaN: isNaN(parsedThreshold),
		}));

		handleChangeSubcondition(index, { meta: { threshold: fieldValue } });

	};

	return (
		<form
			className="tc-rule-editor"
			id={`rule-editor-for-${resource.id}`}
			onSubmit={handleSubmit}>
			{subconditions.map((subcondition, index) => {

				// Get the list of action options for the object type
				const actionOptions = (!overrideActionOptions && actionMap[subcondition.object_type]) ||
					[{ label: subcondition.action, value: subcondition.action }];

				// Check if this is an optional object type for a custom requirement
				const isObjectTypeOption = objectTypeOptionsMap &&
					objectTypeOptionsMap[subcondition.object_type];

				// Get the list of object options for the object type
				const objectOptions = objectOptionsMap &&
					objectOptionsMap[subcondition.object_type];

				// Check if this subcondition object type requires a parent object
				const requireParentObject = objectTypeMap[subcondition.object_type].requiresParent;

				const showThreshold = subcondition.meta &&
					subcondition.action === LRS_ACTIONS.EARNED &&
					subcondition.object_type === LRS_OBJECT_TYPES.POINT;

				return (
					<Row
						// eslint-disable-next-line react/no-array-index-key
						key={index}
						className="tc-rule-editor-subcondition align-items-center mb-3">
						{/* Choose if the subcondition is active */}
						<Col md={1}>
							<Label
								for={`subcondition-${index}-active`}
								hidden>
								<FormattedMessage
									{...messages.TC_COMPONENTS_RULE_EDITOR_FIELD_ACTIVE_LABEL_TEXT}
									values={{ index: index + 1 }} />
							</Label>
							<CheckboxInput
								name={`subcondition-${index}-active`}
								value={subcondition.active}
								ariaLabel={intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_FIELD_ACTIVE_LABEL_TEXT)}
								isEditing
								onChange={() =>
									handleChangeSubcondition(index, { active: !subcondition.active })
								} />
						</Col>
						{/* Choose the subcondition action */}
						{actionOptions.length === 1 &&
							<Col md={3}>
								<span>
									{actionOptions[0].label}
								</span>
							</Col>
						}
						{actionOptions.length > 1 &&
							<Col md={3}>
								<Select
									className="actions-select"
									classNamePrefix="select"
									name={`subcondition-${index}-action`}
									options={actionOptions}
									defaultValue={actionOptions.find(
										(option) => option.value === subcondition.action
									)}
									onChange={({ value }) => onSelectAction(index, 'action', value)} />
							</Col>
						}
						{showThreshold &&
							<Col md={3}>
								<Label
									for={`subcondition-${index}-threshold`}
									hidden>
									<FormattedMessage
										{...messages.TC_COMPONENTS_RULE_EDITOR_FIELD_THRESHOLD_LABEL_TEXT}
										values={{ index: index + 1 }} />
								</Label>
								<Input
									type="number"
									name={`subcondition-${index}-threshold`}
									value={subcondition.meta.threshold}
									ariaLabel={intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_FIELD_THRESHOLD_LABEL_TEXT)}
									isInvalid={formValidation.thresholdNaN}
									error={intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_FIELD_THRESHOLD_INVALID_TEXT)}
									isEditing
									onChange={(fieldValue) => handleChangeThreshold(
										index,
										`subcondition-${index}-threshold`,
										fieldValue,
									)} />
							</Col>
						}
						{/* Choose the subcondition object_type */}
						{isObjectTypeOption &&
							<Col md={3}>
								<Select
									className="object-types-select"
									classNamePrefix="select"
									name={`subcondition-${index}-object_type`}
									options={objectTypeOptions}
									defaultValue={objectTypeOptions.find(
										(option) => option.value === subcondition.object_type
									)}
									onChange={({ value }) => onSelectObjectType(index, 'object_type', value)} />
							</Col>
						}
						{!isObjectTypeOption &&
							<Col md={3}>
								<span>
									{objectTypeMap[subcondition.object_type].label}
								</span>
							</Col>
						}
						{/* Choose the subcondition object_id */}
						{objectOptions && !requireParentObject &&
							<Col md={4}>
								<Select
									key={subcondition.object_type}
									className="objects-select"
									classNamePrefix="select"
									name={`subcondition-${index}-object_id`}
									options={objectOptions}
									defaultValue={objectOptions.find(
										(option) => option.value === subcondition.object_id
									)}
									onChange={({ value }) =>
										onSelectObject(index, 'object_id', value, objectOptions, false)
									} />
							</Col>
						}
						{!objectOptions && subcondition.object_id && subcondition.object_title &&
							<Col md={4}>
								<span>
									{subcondition.object_title}
								</span>
							</Col>
						}
						{/* Choose the subcondition parent_object_id */}
						{objectOptions && requireParentObject &&
							<Fragment>
								<Col
									className="text-center"
									md={1}>
									<FormattedMessage
										{...messages.TC_COMPONENTS_GENERAL_TEXT_LRS_PARENT_DISPLAY} />
								</Col>
								<Col md={4}>
									<Select
										key={subcondition.object_type}
										className="parent-objects-select"
										classNamePrefix="select"
										name={`subcondition-${index}-parent_object_id`}
										options={objectOptions}
										defaultValue={objectOptions.find(
											(option) => option.value === subcondition.parent_object_id
										)}
										onChange={({ value }) =>
											onSelectObject(index, 'parent_object_id', value, objectOptions, true)
										} />
								</Col>
							</Fragment>
						}
					</Row>
				);

			})}
			{allowCustomRequirements && Object.keys(objectOptionsMap).length > 0 &&
				<FormGroup>
					<Button
						color="neutral"
						title={
							intl.formatMessage(messages.TC_COMPONENTS_RULE_EDITOR_ADDITIONAL_REQUIREMENTS_BUTTON_TEXT)
						}
						outline
						onClick={addRequirement}>
						<i
							aria-hidden="true"
							className="fal fa-circle-plus"></i>
						{' '}
						<FormattedMessage
							{...messages.TC_COMPONENTS_RULE_EDITOR_ADDITIONAL_REQUIREMENTS_BUTTON_TEXT} />
					</Button>
				</FormGroup>
			}
			<FormGroup className="my-4">
				<Button
					color="primary"
					type="submit">
					<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SAVE_LABEL} />
				</Button>
				{onCancel &&
					<Button
						className="ms-3"
						color="neutral"
						type="button"
						outline
						onClick={onCancel}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_CANCEL_LABEL} />
					</Button>
				}
			</FormGroup>
		</form>
	);

};

RuleEditorForm.propTypes = propTypes;

export default RuleEditorForm;

/**
 * Defines the list of possible completion requirements,
 * i.e. subconditions, for the completion rule.
 *
 * @param   {object} initialSubconditions  The initial list of possible completion requirements.
 * @param   {object} customSubconditions   The existing list of custom completion requirements.
 * @param   {object} rule                  The existing rule for the resource (if any).
 *
 * @returns {array}  data  The list of initialized subconditions
 *                         defining the possible completion requirements.
 */
function getInitialSubconditions(initialSubconditions = [], customSubconditions = [], rule) {

	const data = [...initialSubconditions];

	// Check if there is an existing completion rule to use to initialize the completion requirements
	if (rule && rule.condition && rule.condition.subconditions) {

		// Loop through each of the possible completion requirements
		for (let i = 0, len = data.length; i < len; i++) {

			const requirement = Object.assign({}, data[i]);

			// Check if there is an existing subcondition for the requirement that is already active
			const activeSubcondition = rule.condition.subconditions.find((subcondition) => {

				// Make sure the subcondition is active
				if (!subcondition.active) {
					return false;
				}

				// Make sure the object type matches
				if (subcondition.object_type !== requirement.object_type) {
					return false;
				}

				// Make sure the object id matches
				if ((!subcondition.object_id && requirement.object_id) ||
					(subcondition.object_id && !requirement.object_id) ||
					(subcondition.object_id !== requirement.object_id)) {
					return false;
				}

				// Make sure the parent object type matches
				if ((!subcondition.parent_object_type && requirement.parent_object_type) ||
					(subcondition.parent_object_type && !requirement.parent_object_type) ||
					(subcondition.parent_object_type !== requirement.parent_object_type)) {
					return false;
				}

				// Make sure the parent object id matches
				if ((!subcondition.parent_object_id && requirement.parent_object_id) ||
					(subcondition.parent_object_id && !requirement.parent_object_id) ||
					(subcondition.parent_object_id !== requirement.parent_object_id)) {
					return false;
				}

				return true;

			});

			// If so, use the existing subcondition to initialize the values of the completion requirement
			if (activeSubcondition) {

				requirement.active = true;
				requirement.action = activeSubcondition.action;
				requirement.meta = activeSubcondition.meta;

				data[i] = requirement;

			}

		}

	}

	return data.concat(customSubconditions);

}
